import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import { VAPermissions, docHeaderStatusDescription } from 'Utils/constants'
import { ListPaymentTypes } from '../ListPaymentType'
import { Formatter } from 'Utils/Formatter'
import ToolTipAmount from 'Components/ToolTipAmount/ToolTipAmount'
import { useTotalMargin } from 'Hooks/useTotalMargin'
import { marginTotal, sanitizeOrderNumber } from 'Utils/functions'
import useValidatePermission from 'Hooks/useValidatePermission'
import YappyPaymentInfo from 'Components/YappyPaymentInfo'
import { getYappyPayments } from 'Utils/yappyPayments'
import GenerateSalesOrder from 'Components/SalesOrder/GenerateSalesOrder'
import { updateOdvPaymentMethod } from 'api/documentBus'
import { useUploadOdcFile } from '../../hooks/useUploadOdcFile'
import { t } from 'i18next'
import { hasPendingOrEmptyPaymentMethod } from '../../utils/paymentMethodSelection'
import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'

export const PaymentType = ({
  payments,
  totalPayment,
  statusPayment,
  onPaymentMethodSaved,
}) => {
  const { sellOrderPostData } = useSelector(state => state.sellOrder)
  const { uploadFile } = useUploadOdcFile()
  const [showSelectPaymentMethod, setShowSelectPaymentMethod] =
    React.useState(false)

  const [visibilityCostMargin, setVisibilityCostMargin] = React.useState(true)

  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const { totalPrice, totalCost } = useTotalMargin(
    sellOrderPostData?.Data?.SalesDocumentInfo?.Lines
  )
  const {
    Amount,
    Tax,
    AmountWithTax,
    Origin,
    CreditAccountNumber,
    PaymentMethod,
    DescriptionStatus,
  } = sellOrderPostData?.Data?.SalesDocumentInfo || {}

  const isWebDocument = isWebDocumentOrigin(Origin)
  const showPayments = isWebDocument
    ? !!payments?.length || Boolean(statusPayment)
    : !!AmountWithTax || Boolean(statusPayment)
  const hasYappyPayment = React.useMemo(
    () => getYappyPayments(payments).length > 0 || Boolean(statusPayment),
    [payments, statusPayment]
  )

  const isDocumentClosedOrCanceled = [
    docHeaderStatusDescription.closed,
    docHeaderStatusDescription.canceled,
  ].includes(DescriptionStatus)

  const showSelectPaymentMethodButton =
    isWebDocument &&
    !isDocumentClosedOrCanceled &&
    hasPendingOrEmptyPaymentMethod(PaymentMethod ?? payments)

  const processPaymentMethodSelection = async values => {
    const isComercialCredit = values.credit === 'yes'

    await updateOdvPaymentMethod({
      saleOrderId: sellOrderPostData?.Data?.SalesDocumentInfo?.DocHeaderId,
      isCommercialCredit: isComercialCredit,
      amount: sellOrderPostData?.Data?.SalesDocumentInfo?.AmountWithTax,
      orderNumber: isComercialCredit
        ? sanitizeOrderNumber(values.orderNumber)
        : null,
      creditAccount: isComercialCredit ? values.accountItemSelected : null,
    })

    if (isComercialCredit && values.file) {
      try {
        await uploadFile({
          file: values.file,
          docData: sellOrderPostData?.Data?.SalesDocumentInfo,
        })
      } catch (uploadError) {
        console.error('Error uploading purchase order file', uploadError)
        // Propagate error so the user sees feedback in the modal.
        // The payment method was saved but the file upload failed.
        throw new Error(t('GENERATE_SALES_ORDER_T.UPLOAD_FILE_ERROR'))
      }
    }

    setShowSelectPaymentMethod(false)
    onPaymentMethodSaved?.()
  }

  return (
    <div
      style={{ display: 'flex', gap: '16px' }}
      data-testid="container-payment-section">
      <div
        style={{ ...styles.containerStyles, width: '60%' }}
        data-testid="container-payment-methods">
        {showSelectPaymentMethodButton && (
          <button
            type="button"
            className="btn-select-payment-method"
            data-testid="btn-select-payment-method"
            style={styles.selectPaymentMethodButton}
            onClick={() => setShowSelectPaymentMethod(true)}>
            {t('GENERATE_SALES_ORDER_T.SELECT_PAYMENT_METHOD_BUTTON_LABEL')}
          </button>
        )}
        <Modal
          open={showSelectPaymentMethod}
          data-testid="modal-select-payment-method-wrapper">
          <Box>
            <GenerateSalesOrder
              setShowModalGenerateSalesOrder={setShowSelectPaymentMethod}
              processSalesOrder={processPaymentMethodSelection}
              paymentSelectionMode
              initialOrderNumber={
                sellOrderPostData?.Data?.SalesDocumentInfo?.OrderNumber
              }
              customerId={
                sellOrderPostData?.Data?.SalesDocumentInfo?.CustomerId
              }
              companyId={sellOrderPostData?.Data?.SalesDocumentInfo?.CompanyId}
            />
          </Box>
        </Modal>
        {!showSelectPaymentMethodButton &&
          showPayments &&
          (hasYappyPayment ? (
            <YappyPaymentInfo
              payments={payments}
              totalPayment={totalPayment ?? AmountWithTax}
              statusPayment={statusPayment}
            />
          ) : (
            <>
              <p style={{ fontSize: '14px', fontWeight: '600' }}>
                Métodos de pago
              </p>
              {!isWebDocument && CreditAccountNumber && (
                <p style={{ fontSize: '14px' }}>
                  Crédito Comercial {CreditAccountNumber}: $
                  {Formatter.Money(AmountWithTax, {
                    rounded: true,
                    decimalPrecision: 2,
                  })}
                </p>
              )}
              {!isWebDocument && !CreditAccountNumber && (
                <p style={{ fontSize: '14px' }}>
                  Contado: $
                  {Formatter.Money(AmountWithTax, {
                    rounded: true,
                    decimalPrecision: 2,
                  })}
                </p>
              )}
              <ListPaymentTypes payments={payments} />
            </>
          ))}
      </div>
      <div
        style={{ ...styles.containerStyles, width: '40%' }}
        data-testid="container-totals">
        <div style={styles.priceStyles} data-testid="container-subtotal">
          <h3 style={{ fontSize: '14px' }}>Sub Total</h3>
          <p style={{ fontSize: '14px', marginRight: 15 }}>
            ${Formatter.Money(Amount)}
          </p>
        </div>
        <div style={styles.priceStyles} data-testid="container-tax">
          <h3 style={{ fontSize: '14px' }}>ITBMS</h3>
          <ToolTipAmount amount={Tax} />
        </div>
        {seeMargenProd && (
          <div style={styles.priceStyles} data-testid="container-margin-total">
            <h3 style={{ fontSize: '14px', fontWeight: '600' }}>
              Margen total
            </h3>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              <span style={{ fontSize: '14px' }}>
                {visibilityCostMargin
                  ? '****'
                  : `${marginTotal(totalCost, totalPrice).toFixed(2)}%`}
              </span>

              <button
                type="button"
                style={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={() => setVisibilityCostMargin(prev => !prev)}>
                <FontAwesomeIcon
                  icon={visibilityCostMargin ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>
        )}
        <div style={styles.priceStyles} data-testid="container-total">
          <h3 style={{ fontSize: '14px', fontWeight: '600' }}>Total</h3>
          <ToolTipAmount amount={AmountWithTax} />
        </div>
      </div>
    </div>
  )
}

PaymentType.propTypes = {
  payments: PropTypes.array,
  totalPayment: PropTypes.number,
  statusPayment: PropTypes.string,
  onPaymentMethodSaved: PropTypes.func,
}

const styles = {
  containerStyles: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    background: '#FFFFFF',
    borderRadius: '5px',
    marginTop: '15px',
    boxShadow: '0px 1px 4px rgba(21, 34, 50, 0.14)',
    padding: '16px 53px',
    height: 'auto',
  },
  priceStyles: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
  },
  selectPaymentMethodButton: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--main-color)',
    backgroundColor: 'var(--secondary-color)',
    border: 'none',
    borderRadius: '5px',
    padding: '10px 18px',
    cursor: 'pointer',
  },
}
